// src/features/fx-ranking/components/TierRadar.tsx
//
// FX liquidity-tier factor radar. Each spoke is a tier (Tier 1 / Tier 2 /
// Tier 3); the three overlaid series are the tier-average Momentum /
// Commercials (COT) / Speculators (COT) z-scores returned by
// GET /api/v1/fx-ranking/tier-summary. Analogous to the commodities
// CategoryRadar — just the taxonomy label changes.

import {
    Legend,
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    Radar,
    RadarChart,
    ResponsiveContainer,
    Tooltip,
} from 'recharts';

import type { TierSummaryRow } from '../api';

interface TierRadarProps {
    tiers: TierSummaryRow[];
}

interface RadarDatum {
    tier: string;
    Momentum: number;
    Commercials: number;
    Speculators: number;
}

const RadarTooltip = ({
    active,
    payload,
    label,
}: {
    active?: boolean;
    payload?: { name: string; value: number; color: string }[];
    label?: string;
}) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs shadow-lg">
            <p className="font-bold text-slate-900 dark:text-white">{label}</p>
            {payload.map((p) => (
                <p key={p.name} style={{ color: p.color }}>
                    {p.name}: <span className="font-mono">{p.value.toFixed(2)}</span>
                </p>
            ))}
        </div>
    );
};

export const TierRadar = ({ tiers }: TierRadarProps) => {
    if (!tiers || tiers.length === 0) {
        return (
            <p className="muted text-sm">No tier aggregates available yet.</p>
        );
    }

    const data: RadarDatum[] = tiers.map((t) => ({
        tier: t.tier,
        Momentum: Number(t.avg_momentum_z.toFixed(3)),
        Commercials: Number(t.avg_fundamental_z.toFixed(3)),
        Speculators: Number(t.avg_sentiment_z.toFixed(3)),
    }));

    return (
        <div
            className="w-full h-96 text-slate-500 dark:text-slate-400"
            role="img"
            aria-label="Average factor z-scores by FX liquidity tier"
        >
            <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={data} outerRadius="72%">
                    <PolarGrid className="stroke-slate-200 dark:stroke-slate-700" />
                    <PolarAngleAxis
                        dataKey="tier"
                        tick={{ fontSize: 11, fill: 'currentColor' }}
                    />
                    <PolarRadiusAxis
                        angle={90}
                        tick={{ fontSize: 10, fill: 'currentColor' }}
                    />
                    <Radar
                        name="Momentum"
                        dataKey="Momentum"
                        stroke="#f59e0b"
                        fill="#f59e0b"
                        fillOpacity={0.15}
                    />
                    <Radar
                        name="Commercials"
                        dataKey="Commercials"
                        stroke="#0ea5e9"
                        fill="#0ea5e9"
                        fillOpacity={0.15}
                    />
                    <Radar
                        name="Speculators"
                        dataKey="Speculators"
                        stroke="#10b981"
                        fill="#10b981"
                        fillOpacity={0.15}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Tooltip content={<RadarTooltip />} />
                </RadarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default TierRadar;
